import { add, endOfWeek, format, isAfter, isBefore, isWithinInterval, startOfDay, startOfWeek, sub } from 'date-fns'

import { TBookingCart, TBookingContext } from '@providers/booking/types'
import type { TShowOffersResultsPetGroup } from '@shared/booking_groups.d'
import { maximumSelectableDate } from '@shared/service_types'
import { compareTime, DEFAULT_TIMEZONE, getAvailableProviderSlots, todayInTZ } from 'components-ui/src/shared/dates'
import { hasNotRequestDates, hasRequestTime, hasShowPreferredProvider } from 'components-ui/src/shared/location'
import { Types } from 'service-api'
import { ANY_GROOMER_ID } from '../helper'
import { TPetOffers } from '../types'

const isInRange = (value: number, min?: number | null, max?: number | null) => {
  if (min && max) {
    return min <= value && max > value
  }

  if (min) {
    return min <= value
  }

  if (max) {
    return max > value
  }

  return false
}

export const getTotalDuration = ({ groups: [group] = [] }: TBookingCart) =>
  group?.pets.reduce((totalDuration, { weight }, idx) => {
    const petWeight = parseInt(weight) || 0
    const grooms = group.grooms[idx]?.offers || []

    return (
      totalDuration +
      grooms.reduce((total, { offer }) => {
        const { serviceOffers = [] } = offer || {}
        const { serviceResources = [] } = serviceOffers[0]?.service || {}

        return (
          total +
          (serviceResources.find(({ restrictionRelations = [] }) =>
            restrictionRelations.some(
              ({ restriction }) =>
                restriction?.type === 'WEIGHT' && isInRange(petWeight, restriction?.min, restriction?.max)
            )
          )?.minutesPerCycle || 0)
        )
      }, 0)
    )
  }, 0) || 0

const checkProviderAvailability = (
  provider: Types.ProviderAdminDto,
  groomOffer: TPetOffers,
  state: TBookingContext,
  dateToCheck: Date
) => {
  if (!provider) {
    return false
  }

  const { available, remainingMinutes, slots } = provider
  const requiredMinutes =
    state.cart.groups[groomOffer.groupIdx].groomsReqMinutes[groomOffer.pet.petIdx][provider.id || '']
      ?.requiredMinutes || 0

  if (!available || requiredMinutes > (remainingMinutes ?? 0)) {
    return false
  }

  if (!hasRequestTime(getInStayLBC(state, 'grooming'))) {
    return true
  }

  if (!slots || slots.length === 0) {
    return false
  }

  // Checks if is today and there are slots available after the current time
  const today = todayInTZ(state.config.location?.timezone || DEFAULT_TIMEZONE)
  const todaySelected = format(today, 'yyyy-MM-dd') === format(dateToCheck, 'yyyy-MM-dd')
  return slots.some((slot) => {
    if (!slot.startTime) {
      return false
    }
    // if today is selected, we need to check if the slot is after the current time
    if (todaySelected && !compareTime(slot.startTime, today)) {
      return false
    }
    return true
  })
}

export const isDayAvailable = (
  day: Date,
  groomOffer: TPetOffers,
  state: TBookingContext,
  weekStart: Date,
  weekEnd: Date,
  checkProvider: boolean
) => {
  if (!isWithinInterval(day, { start: weekStart, end: weekEnd })) {
    return false
  }

  const isDaycare = state.serviceTypeName === 'daycare'

  if (isDaycare) {
    if (
      (state.cart.groups[groomOffer.groupIdx].daycareSelected?.findIndex(({ id }) => id === day.getTime()) ?? -1) < 0
    ) {
      return false
    }
  }

  const petAvailability = state.data.groomAvailability?.[groomOffer.pet.id]
  const dateValue = day.getTime()
  const idx = petAvailability?.findIndex(({ id }) => id === dateValue) ?? -1
  if (idx < 0) {
    return false
  }
  const availability = petAvailability?.[idx]?.availability?.availabilities?.[0]

  // if there is no availability for the pet, return false
  if (!availability?.available) {
    return false
  }

  // if we don't need to check provider, we can return true
  if (!checkProvider) {
    return true
  }

  const selectedProvider = groomOffer.resource!

  // if `checkProvider` is true, means that `lbc.showPreferredProvider` is REQUIRED
  // if providers is null or empty, means that all the providers are not setup to book online
  // so we can return true if the selected provider is ANY_GROOMER_ID
  if ((!availability.providers || availability.providers.length == 0) && selectedProvider.id === ANY_GROOMER_ID) {
    return true
  }

  // check if provider selected is available for the selected date
  const provider = availability.providers?.find((provider) => {
    // returns the first available providers, no matter if there are more
    if (selectedProvider.id === ANY_GROOMER_ID) {
      return checkProviderAvailability(provider, groomOffer, state, day)
    }
    return provider.id === selectedProvider.id && checkProviderAvailability(provider, groomOffer, state, day)
  })
  if (!provider) {
    return false
  }

  return true
}

export const buildGroomPetGroup = (
  ctx: TBookingContext,
  groomOffer: TPetOffers,
  isInStay?: boolean
): TShowOffersResultsPetGroup[] => {
  const selectedResourceId = groomOffer.resource?.id
  const isSpecificGroomer = selectedResourceId && selectedResourceId !== ANY_GROOMER_ID

  return [
    {
      locationSpecies: groomOffer.pet.specieId,
      petCount: 1,
      pets: [
        {
          locationPetProfileId: groomOffer.pet.id,
          displayName: groomOffer.pet.name,
          weight: parseInt(groomOffer.pet.weight),
          breedId: groomOffer.pet.breedId,
        },
      ],
      parentOffer: isInStay ? ctx.cart.groups[groomOffer.groupIdx].offerSelected?.offer?.id : undefined,
      overridePrimaryResourceId: isSpecificGroomer ? selectedResourceId : undefined,
    },
  ]
}

export const isDataInCache = (startDate: Date, endDate: Date, state: TBookingContext, petId: string) => {
  const fromDate = startOfDay(startDate)
  const toDate = startOfDay(sub(endDate, { days: 1 }))

  if (toDate < fromDate) {
    return false
  }

  const cache = state.data.groomAvailability?.[petId]
  const cacheLength = cache?.length || 0
  const firstItem = cache?.[0]?.id ?? 0
  const lastItem = cacheLength ? (cache?.[cacheLength - 1]?.id ?? 0) : 0

  return fromDate.getTime() >= firstItem && toDate.getTime() <= lastItem
}

/**
 * Gets a week date range based on the selected date and boundary constraints
 *
 * @param newStartDate - The date selected by the user
 * @param startDate - The earliest possible date in the range
 * @param endDate - The latest possible date in the range
 * @returns A tuple of [fromDate, toDate] representing the valid week range
 */
export const getDateRange = (newStartDate: Date, startDate: Date, endDate: Date): [Date, Date] => {
  let fromDate: Date
  let toDate: Date

  if (isBefore(newStartDate, startDate)) {
    // Use startDate as the beginning
    fromDate = startDate

    // Find the Saturday of the week containing startDate
    toDate = endOfWeek(startDate, { weekStartsOn: 0 })

    // If toDate exceeds endDate, use endDate
    if (isAfter(toDate, endDate)) {
      toDate = endDate
    }
  } else if (isAfter(newStartDate, endDate)) {
    // Use endDate as the end
    toDate = endDate

    // Find the Sunday of the week containing endDate
    fromDate = startOfWeek(endDate, { weekStartsOn: 0 })

    // If fromDate is before startDate, use startDate
    if (isBefore(fromDate, startDate)) {
      fromDate = startDate
    }
  } else {
    // Get the start of week (Sunday)
    fromDate = startOfWeek(newStartDate, { weekStartsOn: 0 })

    // Get the end of week (Saturday)
    toDate = endOfWeek(newStartDate, { weekStartsOn: 0 })

    // Apply boundary constraints
    if (isBefore(fromDate, startDate)) {
      fromDate = startDate
    }

    if (isAfter(toDate, endDate)) {
      toDate = endDate
    }
  }

  return [fromDate, add(toDate, { days: 1 })]
}

export const getBookingDates = (ctx: TBookingContext) => {
  const isStandalone = ctx.serviceTypeName === 'grooming'

  if (isStandalone) {
    return {
      startDate: ctx.startDate!,
      minDate: ctx.startDate!,
      maxDate: maximumSelectableDate(ctx.startDate!, ctx),
    }
  }

  switch (ctx.serviceTypeName) {
    case 'boarding':
      return {
        startDate: ctx.endDate!,
        minDate: ctx.startDate!,
        maxDate: ctx.endDate!,
      }
    case 'daycare': {
      const selectedDates = ctx.cart.groups[0].daycareSelected
      const datesLength = selectedDates?.length ? selectedDates.length - 1 : 0
      return {
        startDate: selectedDates?.[0]?.date ?? ctx.startDate!,
        minDate: selectedDates?.[0]?.date ?? ctx.startDate!,
        maxDate: selectedDates?.[datesLength]?.date ?? ctx.endDate!,
      }
    }
    default:
      return {
        startDate: ctx.startDate!,
        minDate: ctx.startDate!,
        maxDate: ctx.endDate!,
      }
  }
}

export const getProviderSlots = (ctx: TBookingContext, groomOffer: TPetOffers, provider: Types.ProviderAdminDto) => {
  const dateData = ctx.data.groomAvailability?.[groomOffer.pet.id]?.find(
    ({ id }) => id === groomOffer.requestDates[0]?.id
  )
  if (!dateData) {
    return null
  }

  const locationTz = ctx.config?.location?.timezone || DEFAULT_TIMEZONE
  const today = todayInTZ(locationTz)
  return getAvailableProviderSlots(provider, dateData.date, today)
}

export const getAvailableGroomers = (ctx: TBookingContext, groomOffer: TPetOffers) => {
  const dateData = ctx.data.groomAvailability?.[groomOffer.pet.id]?.find(
    ({ id }) => id === groomOffer.requestDates[0]?.id
  )
  if (!dateData) {
    return null
  }
  const isAnyGroomer = ctx.cart.groups[groomOffer.groupIdx].grooms[groomOffer.pet.petIdx].anyGroomer

  return (
    dateData?.availability?.availabilities?.[0]?.providers?.reduce((acc, provider) => {
      if (!provider.available) {
        return acc
      }
      if (isAnyGroomer || provider.id === groomOffer.resource?.id) {
        acc.push(provider)
      }
      return acc
    }, [] as Types.ProviderAdminDto[]) ?? null
  )
}

export const getInStayLBC = (ctx: TBookingContext, serviceTypeName: string) => {
  const standaloneGroom = ctx.serviceTypeName === serviceTypeName
  return standaloneGroom ? ctx.config.serviceType : ctx.data.inStayBookingConf
}

export const willCheckProvider = (lbc: Types.LocationBookingConfClientDto, anyGroomer: boolean) => {
  if (hasRequestTime(lbc)) {
    return true
  }
  if (hasNotRequestDates(lbc)) {
    return false
  }
  if (hasShowPreferredProvider(lbc)) {
    return !anyGroomer
  }
  return false
}
